import React, { useEffect } from 'react'

export default function Links() {
  useEffect(() => {
    document.title = 'Links | Borgs Køreskole I/S'
    const metaDesc = document.querySelector('meta[name="description"]')
    if (metaDesc) {
      metaDesc.setAttribute(
        'content',
        'Nyttige links, formularer og selvbetjening til kørekortelever hos Borgs Køreskole i Kalundborg.'
      )
    }
  }, [])

  const usefulLinks = [
    {
      id: 'gondrive',
      label: "Go'n Drive login",
      url: 'https://gondrive.com/portal',
      description: 'Log ind på elevportalen for at følge dit køreforløb, booke timer og se overblik.',
      icon: 'fa-solid fa-right-to-bracket',
      isPdf: false
    },
    {
      id: 'ansoegning',
      label: 'Ansøgning om kørekort – udskriv formular',
      url: 'https://cdn.fstyr.dk/faerdselsstyrelsen/Media/639223063102384021/kk001-ansoegning-om-koerekort-p23-2026.pdf',
      description: 'Officiel ansøgningsblanket (P23) fra Færdselsstyrelsen til print og udfyldelse.',
      icon: 'fa-solid fa-file-pdf',
      isPdf: true
    },
    {
      id: 'samtykke',
      label: 'Samtykkeerklæring til kørekortelever under 18 år',
      url: 'https://cdn.fstyr.dk/faerdselsstyrelsen/Media/638161826116299623/Samtykkeerkl%C3%A6ring%20ved%20ans%C3%B8gning%20om%20k%C3%B8rekort%20-%20%20Under%2018%20%C3%A5r%20-%20(P23T).pdf',
      description: 'Blanket til forældresamtykke ved ansøgning om kørekort for elever under 18 år.',
      icon: 'fa-solid fa-file-signature',
      isPdf: true
    },
    {
      id: 'fuldmagt',
      label: 'Giv fuldmagt til bestilling af prøver',
      url: '/_assets/vejleding-ny-dk-og-uk.pdf',
      description: 'Vejledning til at give køreskolen fuldmagt til bestilling af teoriprøve og praktisk køreprøve.',
      icon: 'fa-solid fa-file-pdf',
      isPdf: true
    },
    {
      id: 'borgerservice',
      label: 'Bestil tid i Borgerservice',
      url: 'https://reservation.frontdesksuite.com/kalundborg/borgerservice/Home/Index?pageId=9e705649-6b7f-4b8d-aa42-8a167e370e51&culture=da&uiCulture=da',
      description: 'Online tidsbestilling hos Kalundborg Borgerservice til indlevering af ansøgning.',
      icon: 'fa-solid fa-calendar-check',
      isPdf: false
    },
    {
      id: 'laegeerklaering',
      label: 'Digital lægeerklæring',
      url: 'https://www.borger.dk/vaelg-kommune-obligatorisk?actionPageId=065ca8f9-a1f5-4946-ada7-12e163f568df&selfserviceId=3c14cbe7-1aaf-4d05-b71b-0c63b217796b',
      description: 'Opret og send din digitale lægeerklæring via Borger.dk før lægebesøg.',
      icon: 'fa-solid fa-user-doctor',
      isPdf: false
    },
    {
      id: 'teoriproever',
      label: 'Prøv en af Færdselsstyrelsens digitale teoriprøver',
      url: 'https://dtp.fstyr.dk/',
      description: 'Test dine færdigheder med Færdselsstyrelsens officielle digitale demoprøve.',
      icon: 'fa-solid fa-laptop-file',
      isPdf: false
    },
    {
      id: 'bilens-teknik',
      label: 'Bilens teknik',
      url: 'https://kajkagen.dk',
      description: 'Øv kontrolspørgsmål, bliv klar til køreprøven og lær alt om bilens teknik hos Kajkagen.dk.',
      icon: 'fa-solid fa-car',
      isPdf: false
    }
  ]

  return (
    <div className="bg-slate-50 min-h-screen py-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
        {/* Page Header */}
        <div className="text-center max-w-3xl mx-auto">
          <h1 className="font-header text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight">
            Links
          </h1>
          <p className="mt-4 text-slate-600 text-base sm:text-lg leading-relaxed">
            Her finder du nyttige links, officielle formularer og genveje til selvbetjening i forbindelse med dit kørekortforløb.
          </p>
        </div>

        {/* Links Cards List */}
        <div className="space-y-4">
          {usefulLinks.map((item) => (
            <a
              key={item.id}
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-white p-6 sm:p-7 border-2 border-slate-200 hover:border-emerald-600 shadow-sm hover:shadow-md transition-all duration-200 rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-4 block"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0 text-xl group-hover:bg-emerald-600 group-hover:text-white transition-colors">
                  <i className={item.icon} aria-hidden="true"></i>
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="font-header text-lg sm:text-xl font-bold text-slate-900 group-hover:text-emerald-700 transition-colors">
                      {item.label}
                    </h2>
                    {item.isPdf && (
                      <span className="text-[10px] font-bold uppercase tracking-wider bg-rose-100 text-rose-700 px-2 py-0.5 rounded">
                        PDF
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-600 mt-1 leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 text-sm font-bold text-emerald-700 shrink-0 self-end sm:self-center">
                <span>Åbn link</span>
                <i className="fa-solid fa-arrow-up-right-from-square text-xs group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" aria-hidden="true"></i>
              </div>
            </a>
          ))}
        </div>

        {/* Informative Note */}
        <div className="bg-emerald-900 text-white p-6 sm:p-8 rounded-xl shadow-sm space-y-2">
          <div className="flex items-center gap-3">
            <i className="fa-solid fa-circle-info text-[#8cc73d] text-xl" aria-hidden="true"></i>
            <h3 className="font-header text-lg font-bold text-white">Har du spørgsmål til blanketterne?</h3>
          </div>
          <p className="text-sm text-emerald-100 leading-relaxed">
            Husk at vi altid hjælper dig godt i gang med ansøgning og papirarbejde ved holdstart hos Borgs Køreskole i Kalundborg.
          </p>
        </div>
      </div>
    </div>
  )
}
